import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Card, Btn, Disclaimer, InfoTip } from "@/components/common";
import { Gear, Plus, X, Users, Trash, CalendarBlank, BellRinging, Buildings, Warning, CheckCircle, LockSimple } from "@phosphor-icons/react";
import { REIMBURSEMENT_METHODS } from "@/lib/payments";
import { ACCOUNT_529_NAME } from "@/lib/parties";
import { DATE_FORMATS, getDateFormat, setDateFormatPref, fmtDate, STATE_NAMES, stateName } from "@/lib/format";
import { currencyName, curCode, isUSD, isKnownCurrency } from "@/lib/currencies";
import { CurrencySelect } from "@/components/CurrencySelect";
import { useFxCoverage, fxCoverageFor, fxSourceLabel } from "@/lib/fxAdvisory";
import { toast } from "sonner";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { DecisionDialog } from "@/components/DecisionDialog";
import { removeCategoryTitle, removeCategoryBody, REMOVE_CATEGORY_ACTION, couldNotCheckCategoryUse } from "@/lib/categoryRemoval";
import { Switch } from "@/components/ui/switch";
import { saveNonEligibleCategory, rememberCategoryList } from "@/components/AddCategoryInline";

export default function Settings() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [currencies, setCurrencies] = useState([]);
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [noneligCats, setNoneligCats] = useState([]);
  const { data: students } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  // Which currencies convert without anyone typing a rate — answered by the
  // server, which is the only thing that knows (ZEN-108). The browser used to
  // decide this from its own short list and warned 121 pickable currencies as
  // needing a hand-typed rate when the app converts them on its own.
  const { coverage: fxCoverage, unavailable: fxCoverageUnavailable } = useFxCoverage();

  // ZEN-384: the Display currency section may only promise the "worth in my
  // currency" line when the converter can actually produce it. That estimate
  // converts US dollars INTO the home currency, and the Treasury fallback only
  // publishes the other direction — so the deciding question is `fromUsd`,
  // asked of the same server answer that drives the badges above.
  const displayCov = fxCoverageFor(fxCoverage, displayCurrency);
  const estimateKnown = displayCov.state !== "unknown";
  const estimateAvailable = displayCov.state === "auto" && displayCov.fromUsd;

  useEffect(() => { if (settings) {
    const raw = (settings.currencies || []).map((c) => curCode(c)).filter(isKnownCurrency);
    setCurrencies(["USD", ...raw.filter((c) => !isUSD(c))]);
    // `home_currency` is genuinely optional on the server — absent means NOT
    // SET, not US dollars — so the fallback is said here rather than assumed.
    setDisplayCurrency(curCode(settings.home_currency, "USD"));
  } }, [settings]);

  const save = async () => {
    try { await api.put("/settings", { currencies, home_currency: displayCurrency }); toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["settings"] }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // Scroll to hash anchor (e.g. #noneligible-categories) on SPA navigation
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (hash) { setTimeout(() => { document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 300); }
  }, []);

  return (
    <div>
      <PageHeader title="Settings" subtitle="Per-tenant configuration — currencies, exchange rates, and date format."
        actions={<Btn onClick={save} data-testid="save-settings"><Gear size={16} weight="bold" /> Save changes</Btn>} />

      <p className="mb-4 text-xs text-[#6b7280]">These settings apply to your whole family — the beneficiary picker above doesn&apos;t change this page. Non-eligible categories are managed per beneficiary below.</p>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card data-testid="editor-currencies">
          <h3 className="mb-1 text-sm font-bold uppercase tracking-wide text-[#6b7280]">Foreign currency</h3>
          <p className="mb-3 text-xs text-[#6b7280]">How the app handles non-USD expenses.</p>

          <div className="mb-1 text-xs font-bold uppercase tracking-wide text-[#6b7280]">Currencies you use</div>
          <p className="mb-2 text-xs text-[#6b7280]">USD is your base currency (529 withdrawals and reports are in USD) and can&apos;t be removed. Add any foreign currencies your family pays in.</p>
          <p className="mb-2 text-xs text-[#6b7280]">These appear first in the currency picker when you add an expense. Everything else stays available, alphabetically.</p>

          <div className="overflow-hidden rounded-md border border-[#C2C9D1]" data-testid="currency-table">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-[#F4F7FB] text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">
                  <th className="px-3 py-1.5">Currency</th>
                  <th className="px-3 py-1.5">FX status</th>
                  <th className="px-3 py-1.5 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {currencies.map((c) => {
                  const isUsd = c === "USD";
                  const cov = fxCoverageFor(fxCoverage, c);
                  return (
                    <tr key={c} className="border-t border-[#E9E9EB]" data-testid={`currency-row-${c}`}>
                      <td className="px-3 py-1.5">
                        <span className="font-semibold text-[#0A0A0A]">{c}</span>
                        <span className="ml-1.5 text-xs text-[#6b7280]">{currencyName(c)}</span>
                      </td>
                      <td className="px-3 py-1.5">
                        {isUsd ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#034AA2]/10 px-2 py-0.5 text-[10px] font-bold text-[#034AA2]" data-testid={`currency-flag-${c}`}>
                            <LockSimple size={10} weight="bold" /> Base
                          </span>
                        ) : cov.state === "unknown" ? (
                          /* The server has not answered yet, or could not be
                             reached. Say nothing rather than guess — a confident
                             wrong answer here is the whole defect being fixed. */
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#E9E9EB] px-2 py-0.5 text-[10px] font-bold text-[#6b7280]" data-testid={`currency-flag-${c}`}>
                            {fxCoverageUnavailable ? "Not checked" : "Checking\u2026"}
                          </span>
                        ) : cov.state === "auto" ? (
                          <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5" data-testid={`currency-flag-${c}`}>
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#077A39]/10 px-2 py-0.5 text-[10px] font-bold text-[#077A39]">
                              <CheckCircle size={10} weight="fill" /> Auto-convert
                            </span>
                            <span className="text-[10px] text-[#6b7280]" data-testid={`currency-source-${c}`}>{fxSourceLabel(cov.source)}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#D97706]/10 px-2 py-0.5 text-[10px] font-bold text-[#92400E]" data-testid={`currency-flag-${c}`}>
                            <Warning size={10} weight="fill" /> Manual rate
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {!isUsd && (
                          <button onClick={() => setCurrencies(currencies.filter((x) => x !== c))} className="text-[#6b7280] hover:text-[#F01919]" aria-label={`remove ${c}`} data-testid={`currency-remove-${c}`}>
                            <X size={13} weight="bold" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {fxCoverageUnavailable && (
            <p className="mt-2 text-xs text-[#6b7280]" data-testid="fx-coverage-unavailable">
              Couldn&apos;t check which currencies convert automatically just now. Nothing is wrong with your settings &mdash; reload the page to try again.
            </p>
          )}

          <div className="mt-3 w-56">
            <CurrencySelect value="" onChange={(code) => { if (code && !currencies.includes(code)) setCurrencies([...currencies, code]); }} testid="add-currency" className="h-9" exclude={currencies} flat />
          </div>

          <div className="mt-4 border-t border-[#C2C9D1] pt-4">
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#6b7280]">Exchange rate source <InfoTip testid="fx-source-tip" width="w-80">Rates come from the European Central Bank&apos;s daily reference rates, and each rate is locked onto the transaction at entry so it never changes. For a currency the European Central Bank doesn&apos;t publish, the app automatically uses the official US Treasury reporting rate instead.</InfoTip></div>
            <p className="text-xs text-[#6b7280]">European Central Bank — daily reference rates (sourced directly), with an automatic fallback to the official US Treasury reporting rate.</p>
            <p className="mt-1 text-xs text-[#6b7280]" data-testid="fx-source-note">
              You&apos;re asked to enter a rate yourself only when neither source publishes one for that currency. <Link to="/help?article=foreign-currency" className="font-semibold text-[#034AA2] hover:underline">Learn more in the Help Center</Link>
            </p>
          </div>

          <div className="mt-4 border-t border-[#C2C9D1] pt-4">
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#6b7280]">Display currency <InfoTip testid="display-currency-tip" width="w-72">Your home currency. Where a rate is available, totals show an estimate line in this currency at today&apos;s rate, for your reference — the note under the picker says whether your currency has one. Tax reports and 529 figures always remain in USD.</InfoTip></div>
            <p className="mb-3 text-xs text-[#6b7280]">Show a &ldquo;worth in my currency&rdquo; estimate on combined totals.</p>
            <div className="w-56" data-testid="display-currency-picker">
              <CurrencySelect value={displayCurrency} onChange={setDisplayCurrency} testid="display-currency" />
            </div>
            {!isUSD(displayCurrency) && !estimateKnown && (
              /* The server has not answered yet, or could not be reached. Say
                 nothing confident either way — a promise made blind is the
                 ZEN-384 defect, and a refusal made blind is its mirror. */
              <p className="mt-2 text-xs text-[#6b7280]" data-testid="display-estimate-unknown">
                {fxCoverageUnavailable
                  ? `Couldn’t check just now whether an automatic estimate is available for ${displayCurrency}. Reload the page to try again.`
                  : `Checking whether an automatic estimate is available for ${displayCurrency}…`}
              </p>
            )}
            {!isUSD(displayCurrency) && estimateKnown && estimateAvailable && (
              <p className="mt-2 text-xs italic text-[#6b7280]" data-testid="display-estimate-promise">Totals will show an additional &ldquo;≈ {displayCurrency} X at today&apos;s rate&rdquo; line as a convenience estimate.</p>
            )}
            {!isUSD(displayCurrency) && estimateKnown && !estimateAvailable && (
              /* Amber, matching the "Manual rate" badge above: a true statement
                 that asks for the reader's attention, not an error. */
              <p className="mt-2 text-xs text-[#92400E]" data-testid="display-estimate-unavailable">
                No automatic estimate is available for {displayCurrency}.{" "}
                {displayCov.state === "auto"
                  ? `The ${fxSourceLabel(displayCov.source)} rate the app uses for ${displayCurrency} only converts into US dollars, not out of them, so totals will stay in US dollars.`
                  : `Neither rate source the app uses publishes a rate for ${displayCurrency}, so totals will stay in US dollars.`}
              </p>
            )}
          </div>
        </Card>

        <DateFormatCard />
        <TaxResidencyCard />
        <PerBeneficiaryNonEligEditor students={students || []} />
      </div>

      <PartiesRegistry />

      <NotificationsCard />

      <SchoolDirectoryCard />

      <Disclaimer text={settings?.disclaimer} />
    </div>
  );
}

function PartiesRegistry() {
  const qc = useQueryClient();
  const { data: parties } = useQuery({ queryKey: ["parties"], queryFn: async () => (await api.get("/parties")).data });
  const [ownerNick, setOwnerNick] = useState("");
  const [ownerMethod, setOwnerMethod] = useState("");
  const [form, setForm] = useState({ full_name: "", role: "", nickname: "", preferred_reimbursement_method: "" });
  const methods = parties?.reimbursement_methods || REIMBURSEMENT_METHODS;
  useEffect(() => { if (parties?.owner) { setOwnerNick(parties.owner.nickname); setOwnerMethod(parties.owner.preferred_reimbursement_method || ""); } }, [parties]);
  const refresh = () => qc.invalidateQueries({ queryKey: ["parties"] });

  const saveOwner = async () => { try { await api.put("/parties/owner/nickname", { nickname: ownerNick, preferred_reimbursement_method: ownerMethod }); toast.success("Owner saved"); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const addParty = async () => {
    if (!form.full_name || !form.nickname) { toast.error("Name and nickname required"); return; }
    try { await api.post("/parties", form); toast.success("Party added"); setForm({ full_name: "", role: "", nickname: "", preferred_reimbursement_method: "" }); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const updateParty = async (p, patch) => { try { await api.put(`/parties/${p.id}`, { full_name: p.full_name, role: p.role || "", nickname: p.nickname, preferred_reimbursement_method: p.preferred_reimbursement_method || "", ...patch }); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const [delPartyTarget, setDelPartyTarget] = useState(null);
  const delParty = async (id) => { await api.delete(`/parties/${id}`); toast.success("Removed"); refresh(); setDelPartyTarget(null); };

  return (<>
    <Card className="mt-4" data-testid="parties-registry">
      <h3 className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#6b7280]"><Users size={16} weight="bold" /> Parties registry</h3>
      <p className="mb-3 text-xs text-[#6b7280]">Named people who can pay for or reimburse expenses. Nicknames are shown everywhere. A preferred reimbursement method pre-fills private settlements (always overridable). Built-in: the Owner, each Beneficiary, and the {ACCOUNT_529_NAME}.</p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-[#C2C9D1] p-3">
          <div className="text-[11px] font-bold uppercase text-[#6b7280]">Owner (built-in)</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input value={ownerNick} onChange={(e) => setOwnerNick(e.target.value)} data-testid="owner-nickname" className="h-9 flex-1 rounded-md border border-[#C2C9D1] px-2 text-sm" />
            <select value={ownerMethod} onChange={(e) => setOwnerMethod(e.target.value)} data-testid="owner-method" className="h-9 rounded-md border border-[#C2C9D1] bg-white px-2 text-sm">
              <option value="">No preferred method</option>
              {methods.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <Btn variant="ghost" className="h-9" onClick={saveOwner} data-testid="save-owner-nick">Save</Btn>
          </div>
        </div>
        <div className="rounded-lg border border-[#C2C9D1] bg-[#E9E9EB] p-3">
          <div className="text-[11px] font-bold uppercase text-[#6b7280]">{ACCOUNT_529_NAME} (built-in)</div>
          <div className="mt-1 text-sm font-semibold">{ACCOUNT_529_NAME} — may only pay or reimburse 529-eligible expenses</div>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {(parties?.custom || []).map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-[#C2C9D1] bg-white px-3 py-2 text-sm" data-testid={`party-${p.id}`}>
            <b>{p.nickname}</b><span className="text-xs text-[#6b7280]">{p.full_name}{p.role ? ` · ${p.role}` : ""}</span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase text-[#6b7280]">Reimburse via</span>
              <select value={p.preferred_reimbursement_method || ""} onChange={(e) => updateParty(p, { preferred_reimbursement_method: e.target.value })} data-testid={`party-method-${p.id}`} className="h-8 rounded-md border border-[#C2C9D1] bg-white px-2 text-sm">
                <option value="">No preferred</option>
                {methods.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <button onClick={() => setDelPartyTarget(p.id)} className="text-[#F01919]" aria-label="remove"><Trash size={14} weight="bold" /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 rounded-lg border border-dashed border-[#C2C9D1] p-3 sm:grid-cols-5">
        <input placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="party-fullname" className="h-9 rounded-md border border-[#C2C9D1] px-2 text-sm" />
        <input placeholder="Role (optional)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="party-role" className="h-9 rounded-md border border-[#C2C9D1] px-2 text-sm" />
        <input placeholder="Nickname *" value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} data-testid="party-nickname" className="h-9 rounded-md border border-[#C2C9D1] px-2 text-sm" />
        <select value={form.preferred_reimbursement_method} onChange={(e) => setForm({ ...form, preferred_reimbursement_method: e.target.value })} data-testid="party-method" className="h-9 rounded-md border border-[#C2C9D1] bg-white px-2 text-sm">
          <option value="">Reimburse via…</option>
          {methods.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <Btn variant="green" className="h-9" onClick={addParty} data-testid="add-party-btn"><Plus size={14} weight="bold" /> Add party</Btn>
      </div>
    </Card>
    <DeleteConfirmDialog open={!!delPartyTarget} onClose={() => setDelPartyTarget(null)} onConfirm={() => delParty(delPartyTarget)}
      title="Delete this party?" description="Expenses referencing this party will keep their data but the party name will no longer resolve." />
  </>);
}


function PerBeneficiaryNonEligEditor({ students }) {
  const qc = useQueryClient();
  const active = (students || []).filter((s) => !s.archived);

  // ONE LABEL AT A TIME, NOT THE WHOLE BENEFICIARY.
  //
  // This used to save the beneficiary's ENTIRE record — name, schools, every
  // published cost-of-attendance figure — just to add or drop one label. Two
  // consequences, both real and both hit in practice:
  //
  //   · whatever another tab had changed on that beneficiary was silently
  //     overwritten, including a category added from an expense form; and
  //   · it did not work AT ALL for a beneficiary whose school start date is a
  //     month with no day, because the full save refuses that record — so this
  //     panel was simply broken for them (ZEN-316).
  //
  // Both go away by asking for what is actually wanted. The add and the remove
  // each change one field, in the store, so a category somebody adds at the same
  // moment is not carried off with the one being removed. (ZEN-56, adversarial
  // review rounds 5 and 6. The WIDER overwrite — every other field on that
  // record, from any screen that saves a beneficiary — is ZEN-333 and is not
  // fixed here.)
  // THE SAME SAVE EVERY OTHER SCREEN USES to add a category (ZEN-512): one
  // save, one list update, so a fix to either is made once.
  const addCategory = async (student, name) => {
    try {
      const { list } = await saveNonEligibleCategory(student.id, name);
      rememberCategoryList(qc, student.id, list);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const removeNow = async (student, name) => {
    try {
      await api.delete(`/students/${student.id}/noneligible-categories`, { params: { name } });
      qc.invalidateQueries({ queryKey: ["students"] });
    } catch (e) { toast.error(apiErr(e)); }
  };

  // ASK FIRST WHEN EXPENSES USE IT (ZEN-372). The number comes from the server's
  // count of stored expenses, never from what this screen loaded. If it cannot
  // be read, nothing is removed: "could not tell" is not zero.
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  // ONE REMOVAL AT A TIME: a second click while one is being checked or asked
  // about is ignored, so a late answer can never swap the question under the
  // reader. (Outside review, round 2.)
  const inFlight = useRef(false);
  const release = () => { inFlight.current = false; };
  const removeCategory = async (student, name) => {
    if (inFlight.current) return;
    inFlight.current = true;
    let count;
    try {
      const { data } = await api.get(`/students/${student.id}/noneligible-categories/usage`, { params: { name } });
      count = data?.expense_count;
    } catch { count = undefined; }
    if (!Number.isInteger(count) || count < 0) { toast.error(couldNotCheckCategoryUse(name)); release(); return; }
    if (count === 0) { await removeNow(student, name); release(); return; }
    setPending({ student, name, count });   // released when the question closes
  };
  const closeQuestion = () => { setPending(null); release(); };
  const confirmRemove = async () => {
    if (!pending) return;
    setBusy(true);
    await removeNow(pending.student, pending.name);
    setBusy(false);
    closeQuestion();
  };

  return (<>
    <DecisionDialog open={!!pending} onClose={() => { if (!busy) closeQuestion(); }} onConfirm={confirmRemove}
      title={pending ? removeCategoryTitle(pending.name, pending.student) : ""}
      actionLabel={REMOVE_CATEGORY_ACTION} busy={busy} testid="remove-category">
      {pending && <p className="text-sm text-[#374151]" data-testid="remove-category-body">{removeCategoryBody(pending.name, pending.student, pending.count)}</p>}
    </DecisionDialog>
    <Card data-testid="noneligible-categories-card" id="noneligible-categories">
      <h3 className="mb-1 flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#6b7280]">Non-eligible categories by beneficiary <InfoTip testid="noneligible-info" width="w-80">Keep it short, precise, and all-inclusive — one label that covers everything for that activity, e.g., &quot;Wrestling&quot; for its fees, equipment, and travel. You can be more granular if you prefer, but keep it brief: the label appears in your expense table.</InfoTip></h3>
      <p className="mb-4 text-xs text-[#6b7280]">Custom labels for expenses that are NOT 529-eligible (e.g., sports or club fees). They appear at the top of the category list when you add an expense for that beneficiary, and they never enter your tax reports.</p>
      {active.length === 0 && <p className="text-xs text-[#9aa1a9]">No active beneficiaries.</p>}
      <div className="space-y-4">
        {active.map((s) => (
          <BeneficiaryCatEditor key={s.id} student={s}
            onAdd={(name) => addCategory(s, name)} onRemove={(name) => removeCategory(s, name)} />
        ))}
      </div>
    </Card>
  </>);
}

function BeneficiaryCatEditor({ student, onAdd, onRemove }) {
  const cats = student.noneligible_categories || [];
  const [v, setV] = useState("");
  // The server decides whether this is a repeat, and it matches without regard
  // to case — so the box no longer refuses "Wrestling" here while the expense
  // form accepts it. Clearing on send rather than on success keeps typing quick;
  // a refusal says so in its own message.
  const add = () => { const x = v.trim().slice(0, 40); if (x) { onAdd(x); setV(""); } };
  const remove = (cat) => onRemove(cat);
  return (
    <div>
      <div className="mb-1 text-xs font-bold text-[#1f2937]">{student.full_name || student.name}</div>
      <div className="flex flex-wrap gap-1.5">
        {cats.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 rounded-full bg-[#6b7280] px-2.5 py-0.5 text-xs font-semibold text-white">
            {c}<button onClick={() => remove(c)} aria-label={`remove ${c}`}><X size={11} weight="bold" /></button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          data-testid={`add-noneligible-${student.id}`} placeholder="Type a non-eligible category, e.g., Wrestling" maxLength={40}
          className="h-8 flex-1 rounded-md border border-[#C2C9D1] px-3 text-xs" />
        <Btn variant="ghost" onClick={add} className="h-8 px-2"><Plus size={13} weight="bold" /></Btn>
      </div>
    </div>
  );
}



function DateFormatCard() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [val, setVal] = useState(getDateFormat());

  // Keep local selection in sync when settings arrive (cross-device)
  useEffect(() => { if (settings?.date_format) setVal(settings.date_format); }, [settings?.date_format]);

  const apply = (v) => {
    setVal(v);
    setDateFormatPref(v);
    api.put("/settings", { date_format: v })
      .then(() => qc.invalidateQueries({ queryKey: ["settings"] }))
      .catch(() => {});
    toast.success("Date format updated");
    // No page reload needed — components re-render reactively via useFmt()
  };
  return (
    <Card data-testid="editor-dateformat">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#6b7280]"><CalendarBlank size={16} weight="bold" /> Date display format</h3>
      <p className="mb-3 text-xs text-[#6b7280]">How dates appear across the app (lists, tables, reports). Today shows as <b>{fmtDate(new Date().toISOString().slice(0, 10), val)}</b>.</p>
      <div className="space-y-2">
        {DATE_FORMATS.map((d) => (
          <label key={d.value} className="flex items-center gap-2 rounded-md border border-[#C2C9D1] px-3 py-2 text-sm font-medium hover:bg-[#E9E9EB]">
            <input type="radio" name="dateformat" checked={val === d.value} onChange={() => apply(d.value)} data-testid={`dateformat-${d.value}`} />
            <span>{d.label} <span className="text-xs text-[#6b7280]">({d.hint})</span></span>
          </label>
        ))}
      </div>
    </Card>
  );
}


function TaxResidencyCard() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [val, setVal] = useState("");
  useEffect(() => { if (settings?.tax_residency_state !== undefined) setVal(settings.tax_residency_state || ""); }, [settings?.tax_residency_state]);
  const apply = (v) => {
    setVal(v);
    api.put("/settings", { tax_residency_state: v || "" })
      .then(() => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Tax residency state updated"); })
      .catch(() => toast.error("Failed to save"));
  };
  const states = settings?.us_states || [];
  return (
    <Card data-testid="editor-tax-residency">
      <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-[#6b7280]">Tax residency state</h3>
      <p className="mb-3 text-xs text-[#6b7280]">The US state where you file your income taxes. Used to personalize state-recapture warnings on non-qualified withdrawals.</p>
      <select className="h-10 w-full max-w-xs rounded-md border border-[#C2C9D1] bg-white px-2 text-sm" value={val} onChange={(e) => apply(e.target.value)} data-testid="tax-residency-select">
        <option value="">— Not set —</option>
        {states.map((s) => <option key={s} value={s}>{s} — {stateName(s)}</option>)}
      </select>
    </Card>
  );
}





function NotificationsCard() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const notifyOn = settings?.notify_on_submission !== false; // default ON

  const toggle = async (val) => {
    try {
      await api.put("/settings", { notify_on_submission: val });
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success(val ? "Submission notifications enabled" : "Submission notifications disabled");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="mt-4" data-testid="notifications-card">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#6b7280]"><BellRinging size={16} weight="bold" /> Notifications</h3>
      <p className="mb-3 text-xs text-[#6b7280]">Control when you receive transactional emails. Password-reset and invitation emails are always sent regardless of these settings.</p>
      <label className="flex items-center gap-3 rounded-md border border-[#C2C9D1] px-3 py-2.5">
        <Switch checked={notifyOn} onCheckedChange={toggle} data-testid="notify-on-submission-toggle" />
        <div>
          <span className="text-sm font-semibold text-[#0A0A0A]">Email me when a receipt is submitted for approval</span>
          {/* WHAT THIS SENTENCE PROMISES IS THE RULE ITSELF, so it moves when
              the rule moves. It used to read "At most one per hour", which
              described a guard that counted receipts and then went quiet for an
              hour — the behaviour ZEN-688 removed. The email now names the
              beneficiary instead of counting anything, and the quiet period is
              per beneficiary rather than per household. A screen still
              promising the old rule is the same defect as a wrong number. */}
          <p className="mt-0.5 text-xs text-[#6b7280]">Receive an email when a beneficiary submits a receipt for your review. One email per beneficiary — then nothing more about that person until you have cleared their receipts or a day has passed.</p>
        </div>
      </label>
    </Card>
  );
}


// READ-ONLY ON PURPOSE (ZEN-367).
//
// This card used to offer two buttons — "Auto-fetch from FSA" and "Upload Excel
// manually" — to every account owner. The school directory is ONE table shared
// by every household, not a copy per family, and loading it replaces the whole
// thing. So any customer could wipe and rewrite the list every other family
// reads, and the codes in it end up on tax paperwork.
//
// Loading it is now an operator action held outside the product, so the buttons
// are gone. What is left is what a customer actually benefits from knowing:
// which edition they are searching against, and whether it is stale.
function SchoolDirectoryCard() {
  const { data: info, isLoading } = useQuery({
    queryKey: ["schools-directory-info"],
    queryFn: async () => (await api.get("/schools-directory/info")).data,
  });

  const fmtDateShort = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
    catch { return iso; }
  };

  return (
    <Card className="mt-4" data-testid="school-directory-card">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#6b7280]">
        <Buildings size={16} weight="bold" /> School directory
      </h3>
      <p className="mb-3 text-xs text-[#6b7280]">
        The Federal School Code List is published by the U.S. Dept. of Education and lists every Title IV-participating institution. Loading it enables school-name autocomplete with eligibility verification.
      </p>

      {isLoading ? (
        <div className="h-8 animate-pulse rounded-md bg-[#E9E9EB]" />
      ) : info?.loaded ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-[#C2C9D1] px-3 py-2 text-sm">
            <CheckCircle size={16} weight="fill" className="text-[#077A39]" />
            <div>
              <span className="font-semibold">{info.edition}</span>
              <span className="ml-2 text-xs text-[#6b7280]">{info.row_count.toLocaleString()} schools · loaded {fmtDateShort(info.ingested_at)}</span>
            </div>
          </div>
          {info.stale && (
            <div className="flex items-start gap-2 rounded-md border border-[#D97706]/40 bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]" data-testid="school-directory-stale-warning">
              <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
              <span>A newer quarterly edition is likely available and we will load it. The list is updated February 1, May 1, August 1, and November 1.</span>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-[#C2C9D1] px-3 py-3 text-center text-xs text-[#6b7280]">
          The school directory has not been loaded yet, so school names will not
          autocomplete. Contact support and we will load it.
        </div>
      )}
    </Card>
  );
}
